import { CartAddEvent } from '@theme/events';

const pendingVariants = new Set();
class FlateriaCartRecommendations extends HTMLElement {
  static observedAttributes = ['data-cart-key'];
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected && this.controller) {
      queueMicrotask(() => this.loadFallback());
    }
  }
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('change', this.selectVariant, { signal });
    this.addEventListener('submit', this.addProduct, { signal });
    this.loadFallback();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  selectVariant = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const option = select.selectedOptions[0];
    const form = select.closest('form');
    form.querySelector('button').disabled = !select.value || option.disabled || pendingVariants.has(select.value);
    if (option.dataset.price) this.querySelector(`[data-product-id="${select.closest('article').dataset.productId}"] [data-recommendation-price]`).textContent = option.dataset.price;
    const compare = select.closest('article').querySelector('[data-recommendation-compare]');
    if (compare) {
      compare.textContent = option.dataset.comparePrice || '';
      compare.hidden = !option.dataset.comparePrice;
    }
  };

  async loadFallback() {
    if (this.dataset.fallback !== 'true') return;
    const cartKey = this.dataset.cartKey;
    const url = new URL(`${window.Shopify.routes.root}recommendations/products`, location.origin);
    url.search = new URLSearchParams({ section_id: 'plateful-cart-upsells', product_id: this.dataset.productId, intent: 'related', limit: '10' });
    try {
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const replacement = html.querySelector('flateria-cart-recommendations');
      // Retain the server-rendered catalog fallback on errors, stale responses, or while choosing/adding a variant.
      if (!this.isConnected || cartKey !== this.dataset.cartKey || !replacement || this.contains(document.activeElement) || this.querySelector('[aria-busy="true"]')) return;
      if (replacement.dataset.cartKey !== cartKey) return;
      this.innerHTML = replacement.innerHTML;
      this.hidden = replacement.hidden;
      this.dataset.fallback = 'false';
    } catch (error) {
      if (error.name !== 'AbortError') console.debug('Cart recommendation fallback unavailable');
    }
  }

  addProduct = async (event) => {
    const form = event.target.closest('[data-recommendation-form]');
    if (!form) return;
    event.preventDefault();
    const button = form.querySelector('button');
    const id = new FormData(form).get('id')?.toString();
    if (!id || button.disabled || pendingVariants.has(id) || !form.reportValidity()) return;
    pendingVariants.add(id);
    button.disabled = true;
    form.setAttribute('aria-busy', 'true');
    button.textContent = 'ADDING…';
    const errorMessage = form.querySelector('[role="alert"]');
    errorMessage.hidden = true;
    let added = false;
    try {
      const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))];
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1 }], sections: sectionIds, sections_url: location.pathname })
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
      added = true;
      button.textContent = 'ADDED';
      let cart;
      try {
        const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
        if (cartResponse.ok) cart = await cartResponse.json();
      } catch { /* The add response still contains authoritative rendered cart sections. */ }
      const sectionHtml = Object.values(result.sections || {}).filter(Boolean).join('');
      const renderedCart = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const count = cart?.item_count ?? Number(renderedCart.querySelector('[ref="cartItemCount"]')?.textContent.trim());
      // Use the theme's existing section-morph/cart event pathway; Shopify renders prices and totals.
      this.dispatchEvent(new CartAddEvent(cart || result, id, { source: 'flateria-cart-recommendations', itemCount: count, productId: form.closest('article').dataset.productId, sections: result.sections }));
      window.setTimeout(() => {
        const status = document.querySelector('[data-pf-cart-status]');
        if (status) {
          status.textContent = 'Added to cart';
          window.setTimeout(() => { if (status.isConnected) status.textContent = ''; }, 2500);
        }
      }, 150);
    } catch (error) {
      errorMessage.textContent = error.message || 'Unable to add this item. Please try again.';
      errorMessage.hidden = false;
    } finally {
      form.removeAttribute('aria-busy');
      if (added) {
        window.setTimeout(() => pendingVariants.delete(id), 1000);
      } else {
        pendingVariants.delete(id);
        button.disabled = false;
        button.textContent = '+ ADD';
      }
    }
  };
}

if (!customElements.get('flateria-cart-recommendations')) {
  customElements.define('flateria-cart-recommendations', FlateriaCartRecommendations);
}
